<template>
  <div class="dcm-dashboard-page">
    <MenuBar />
    <div class="content">
      <div class="page-header">
        <h1>DCM Dashboard</h1>
        <p>New campaign tasks from Marketing/Admin appear here, even if you log in later.</p>
      </div>

      <div class="summary-cards">
        <div class="card">
          <span class="label">New Tasks</span>
          <strong>{{ notifications.length }}</strong>
        </div>
        <div class="card creative-card" v-if="creativeSubmissionCount > 0">
          <span class="label">Creative Submissions</span>
          <strong>{{ creativeSubmissionCount }}</strong>
        </div>
      </div>

      <div class="task-section">
        <div class="task-header">
          <h2>Campaign Ready Queue</h2>
          <button class="refresh-btn" @click="fetchNotifications">Refresh</button>
        </div>

        <div v-if="loading" class="empty-state">Loading tasks...</div>
        <div v-else-if="!notifications.length" class="empty-state">No new tasks available right now.</div>

        <div v-else class="task-list">
          <div
            class="task-item"
            v-for="item in notifications"
            :key="item.id"
            :class="{ 'creative-submission': item.type === 'creative_submission' }"
          >
            <div class="task-body">
              <div class="task-title-row">
                <p class="task-title">{{ item.campaignName || 'Campaign' }}</p>
                <span class="type-badge" v-if="item.type === 'creative_submission'">Creative Submitted</span>
              </div>
              <p class="task-sub">Sponsor: <strong>{{ item.sponsorName || '-' }}</strong></p>
              <p class="task-message">{{ item.message }}</p>
              <p class="task-time">Created: {{ formatDate(item.createdAt) }}</p>

              <div class="details-panel" v-if="selectedCampaignId === item.campaignId && campaignDetails">
                <h4>Campaign Details</h4>
                <div class="details-grid">
                  <p><strong>Brand/Product:</strong> {{ campaignDetails.campaignName || '-' }}</p>
                  <p><strong>Sponsor:</strong> {{ campaignDetails.sponsorName || item.sponsorName || '-' }}</p>
                  <p><strong>Start:</strong> {{ campaignDetails.startDate || '-' }}</p>
                  <p><strong>End:</strong> {{ campaignDetails.endDate || '-' }}</p>
                  <p><strong>Category:</strong> {{ formatCategories(campaignDetails.contentCategory) }}</p>
                  <p><strong>Distribution Ratio:</strong> {{ formatDistribution(campaignDetails) }}</p>
                  <p><strong>Number of Content:</strong> {{ campaignDetails.numberOfContent || '-' }}</p>
                  <p><strong>Daily Content:</strong> {{ campaignDetails.dailyContentCount || '-' }}</p>
                  <p><strong>Platform:</strong> {{ formatPlatform(campaignDetails.videoPlatformDistribution) }}</p>
                  <p><strong>Events:</strong> {{ formatEventTags(campaignDetails.eventTags) }}</p>
                </div>
                <p class="details-note"><strong>Exact Schedule:</strong> {{ formatScheduleSummary(campaignDetails) }}</p>
                <p class="details-note"><strong>Remarks:</strong> {{ campaignDetails.remarks || '-' }}</p>
                <p class="details-note"><strong>Creative:</strong> {{ campaignDetails.creative || '-' }}</p>
                <p class="details-note"><strong>Instruction:</strong> {{ campaignDetails.instruction || '-' }}</p>
                <p class="details-note"><strong>Brandings:</strong> Thumbnail ({{ campaignDetails.brandingThumbnail ? 'Yes' : 'No' }}), Dog ({{ campaignDetails.brandingDog ? 'Yes' : 'No' }}), Static ({{ campaignDetails.brandingStatic ? 'Yes' : 'No' }}), Photocard ({{ campaignDetails.brandingPhotocard ? 'Yes' : 'No' }}), Reels ({{ campaignDetails.brandingReels ? 'Yes' : 'No' }}), Lshape ({{ campaignDetails.brandingLshapeVariations || '-' }}), OVC ({{ campaignDetails.brandingOvcVariations || '-' }})</p>

                <div class="files-wrap">
                  <p class="files-title">Uploaded Requirement Files</p>
                  <div v-if="hasRequirementFiles(campaignDetails.fileRequirements)">
                    <div v-for="(files, label) in campaignDetails.fileRequirements" :key="label" class="file-group" v-if="Array.isArray(files) && files.length">
                      <p class="file-group-label">{{ prettyLabel(label) }}</p>
                      <ul>
                        <li v-for="file in files" :key="file.key || file.url">
                          <a :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name || 'File' }}</a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p v-else class="file-empty">No files uploaded yet.</p>
                </div>
              </div>
            </div>
            <div class="task-actions">
              <button class="details-btn" @click="goToDcmOperation(item)">Proceed</button>
              <button class="details-btn" @click="toggleDetails(item)">
                {{ selectedCampaignId === item.campaignId ? 'Hide Details' : 'Details' }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';

export default {
  name: 'DcmDashboard',
  components: {
    MenuBar,
  },
  data() {
    return {
      loading: false,
      notifications: [],
      selectedCampaignId: '',
      campaignDetails: null,
      detailsLoading: false,
      message: '',
    };
  },
  computed: {
    creativeSubmissionCount() {
      return this.notifications.filter(item => item.type === 'creative_submission').length;
    },
  },
  mounted() {
    this.fetchNotifications();
  },
  methods: {
    async fetchNotifications() {
      this.loading = true;
      this.message = '';
      try {
        const response = await sponsorService.getDcmNotifications({ unreadOnly: true });
        this.notifications = response.items || [];
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load DCM tasks';
      } finally {
        this.loading = false;
      }
    },
    async toggleDetails(item) {
      if (!item.campaignId) {
        this.message = 'Campaign id missing for this task';
        return;
      }

      if (this.selectedCampaignId === item.campaignId) {
        this.selectedCampaignId = '';
        this.campaignDetails = null;
        return;
      }

      this.selectedCampaignId = item.campaignId;
      this.campaignDetails = null;
      this.detailsLoading = true;
      this.message = '';

      try {
        const response = await sponsorService.getDcmCampaignDetails(item.campaignId);
        this.campaignDetails = response.item || null;
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load campaign details';
      } finally {
        this.detailsLoading = false;
      }
    },
    async goToDcmOperation(item) {
      if (!item || !item.campaignId) {
        this.message = 'Campaign id missing for this task';
        return;
      }

      try {
        await sponsorService.acceptDcmCampaignTask(item.campaignId);
        await sponsorService.markDcmNotificationRead(item.id);
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to accept campaign task';
        return;
      }

      this.$router.push({ path: '/dcm-management', query: { campaignId: item.campaignId } });
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    formatCategories(value) {
      if (!value) return '-';
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '-';
      }
      return String(value);
    },
    formatPlatform(value) {
      if (!value) return '-';
      const platform = String(value).toLowerCase();
      if (platform === 'fb') return 'FB';
      if (platform === 'yt') return 'YT';
      if (platform === 'both') return 'Both';
      return value;
    },
    formatDistribution(campaign) {
      const distribution = campaign && campaign.contentDistribution;
      if (distribution && Array.isArray(distribution.items) && distribution.items.length) {
        return distribution.items.map(item => `${item.category}: ${item.percent}%`).join(', ');
      }
      return (campaign && campaign.distributionRatio) || '-';
    },
    formatEventTags(tags) {
      if (!Array.isArray(tags) || !tags.length) return '-';
      return tags.join(', ');
    },
    formatScheduleSummary(campaign) {
      const rows = campaign && Array.isArray(campaign.scheduleEntries) ? campaign.scheduleEntries : [];
      if (!rows.length) return (campaign && campaign.exactSchedule) || '-';
      return rows
        .map(item => {
          const parts = [];
          if (item.date) parts.push(item.date);
          if (item.category) parts.push(item.category);
          if (item.plannedCount) parts.push(`count ${item.plannedCount}`);
          if (item.instruction) parts.push(item.instruction);
          return parts.join(' | ');
        })
        .filter(Boolean)
        .join(', ');
    },
    prettyLabel(label) {
      return String(label || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    hasRequirementFiles(fileRequirements) {
      if (!fileRequirements || typeof fileRequirements !== 'object') return false;
      return Object.values(fileRequirements).some(files => Array.isArray(files) && files.length);
    },
  },
};
</script>

<style scoped>
.dcm-dashboard-page {
  display: flex;
  min-height: 100vh;
  background: #f4f7fb;
}

.content {
  flex: 1;
  margin-left: 250px;
  padding: 24px;
}

.page-header h1 {
  margin: 0;
  color: #1f2d3d;
}

.page-header p {
  margin: 8px 0 16px;
  color: #5f7389;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.card {
  background: #fff;
  border: 1px solid #e1e9f2;
  border-radius: 12px;
  padding: 12px;
}

.card.creative-card {
  border-color: #f5c542;
  background: #fff8e6;
}

.card .label {
  display: block;
  color: #62768e;
  font-size: 12px;
}

.card strong {
  color: #1f3654;
  font-size: 24px;
}

.task-section {
  background: #fff;
  border: 1px solid #e1e9f2;
  border-radius: 12px;
  padding: 14px;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.task-header h2 {
  margin: 0;
  color: #233244;
}

.refresh-btn {
  border: none;
  background: #2c5cc5;
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-item {
  border: 1px solid #e4ebf4;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.details-btn {
  border: 1px solid #2c5cc5;
  border-radius: 8px;
  background: #fff;
  color: #2c5cc5;
  padding: 7px 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.task-item.creative-submission {
  border-color: #f5c542;
  background: #fff8e6;
}

.task-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-badge {
  background: #f5c542;
  color: #6b4e00;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.task-title {
  margin: 0;
  font-weight: 700;
  color: #1f2d3d;
}

.task-sub,
.task-message,
.task-time {
  margin: 4px 0 0;
  color: #5d7289;
  font-size: 13px;
}

.details-panel {
  margin-top: 10px;
  border: 1px solid #d8e3f2;
  border-radius: 10px;
  background: #fbfdff;
  padding: 10px;
}

.details-panel h4 {
  margin: 0 0 8px;
  color: #274368;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
}

.details-grid p,
.details-note {
  margin: 0;
  color: #536a82;
  font-size: 12px;
}

.details-note {
  margin-top: 6px;
}

.files-wrap {
  margin-top: 10px;
  border-top: 1px solid #e2eaf5;
  padding-top: 8px;
}

.files-title {
  margin: 0 0 6px;
  color: #2a466b;
  font-size: 12px;
  font-weight: 700;
}

.file-group {
  margin-bottom: 6px;
}

.file-group-label {
  margin: 0 0 4px;
  font-size: 12px;
  color: #5b738b;
  font-weight: 700;
}

.file-group ul {
  margin: 0;
  padding-left: 16px;
}

.file-group li a {
  color: #1d4ed8;
  font-size: 12px;
}

.file-empty {
  margin: 0;
  font-size: 12px;
  color: #6e8296;
}

.empty-state {
  border: 1px dashed #c5d5e8;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
  color: #61758b;
}

@media (max-width: 768px) {
  .content {
    margin-left: 200px;
    padding: 16px;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }

  .task-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
